import { Inject, Injectable } from '@angular/core'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { environment } from '../../environments/environment'
import { connectFunctionsEmulator, Functions } from '@angular/fire/functions'
import { DOCUMENT } from '@angular/common'
import { GlobalService } from './global.service'
import {
    Client,
    merchantParkingsPath,
    OmnisendAddToCartRequest,
    OmnisendCreateCartRequest,
    OmnisendCreateOrderRequest,
    OmnisendCreateProductRequest,
    OmnisendProduct,
    OmnisendVariant,
    Parking,
} from '@parkupp/core'
import { ClientService } from './client.service'
import { AuthenticationService } from './authentication.service'
import * as moment from 'moment'
import { doc, updateDoc } from '@angular/fire/firestore'

@Injectable({
    providedIn: 'root',
})
export class OmnisendService {
    private readonly COOKIE_NAME = 'omnisendContactID'
    functions: Functions
    private window: any

    constructor(@Inject(DOCUMENT) private document: Document, private globalService: GlobalService, private authService: AuthenticationService, private clientService: ClientService) {
        this.functions = getFunctions()
        if (environment.useEmulators) {
            connectFunctionsEmulator(this.functions, 'localhost', 5004)
        }
        this.window = this.document.defaultView

        this.injectOmnisendSnippet()

        this.clientService.activeClientReady.subscribe((client) => {
            this.updateUserCookie(client as Client)
        })

        this.authService.onSignOut.subscribe(() => {
            this.globalService.deleteCookie(this.COOKIE_NAME)
        })
    }

    /**
     * Inject the Omnisend brand snippet using the env-specific brandID.
     * Previously hardcoded to the production brand in index.html, which
     * caused dev/staging traffic to inflate prod Omnisend metrics.
     */
    private injectOmnisendSnippet() {
        if (!this.window) return

        const brandID = environment.omnisendAccountId
        if (!brandID) return
        if (this.window.__omnisendBootstrapped) return
        this.window.__omnisendBootstrapped = true

        this.window.omnisend = this.window.omnisend || []
        this.window.omnisend.push(['brandID', brandID])
        this.window.omnisend.push(['track', '$pageViewed'])

        const script = this.document.createElement('script')
        script.type = 'text/javascript'
        script.async = true
        script.src = 'https://omnisnippet1.com/inshop/launcher-v2.js'
        const first = this.document.getElementsByTagName('script')[0]
        first?.parentNode?.insertBefore(script, first)
    }

    /**
     * Track product view in Omnisend
     * Note: This is fire-and-forget - errors are logged but never thrown
     * to ensure tracking failures never block user flows
     */
    async productView(parking: Parking) {
        try {
            await this.upsertProduct(parking)

            const productData = {
                $productID: parking.$key,
                $currency: 'ZAR',
                $price: parking.defaultPriceInCents,
                $title: parking.title,
                $variantID: parking.$key,
                $imageUrl: parking.defaultImage,
                $productUrl: `${environment.host}/parking/${parking.$key}`,
            }
            if (this.window?.omnisend?.push) {
                this.window.omnisend.push(['track', '$productViewed', productData])
            }
        } catch (error) {
            // Log but never throw - tracking should never block user flows
            console.warn('[Omnisend] Error in productView (non-blocking):', error)
        }
    }

    /**
     * Sync a parking to Omnisend as a product. Picks PUT vs POST based on
     * whether parking.omnisendProductId has been persisted, eliminating the
     * routine 409 'product already exists' noise we previously got from
     * always POSTing first.
     */
    private async upsertProduct(parking: Parking): Promise<void> {
        if (!parking.defaultImage) {
            console.warn('[Omnisend] Cannot sync product - parking has no default image (non-blocking)')
            return
        }

        if (parking.omnisendProductId) {
            await this.updateProduct(parking)
            return
        }

        const result = await this.createProduct(parking)
        const productId = result?.data?.data?.id || parking.$key
        if (!productId) return

        const update = {
            omnisendProductId: productId,
            updatedAt: new Date(),
        }
        const docRef = doc(this.clientService.firestore, merchantParkingsPath(parking.merchantRef.id), parking.$key!)
        await updateDoc(docRef, update)
    }

    private buildProductRequest(parking: Parking): OmnisendCreateProductRequest {
        const omnisendVariant = new OmnisendVariant({
            id: parking.$key,
            title: parking.title,
            status: 'inStock',
            price: parking.defaultPriceInCents / 100,
            url: `${environment.host}/parking/${parking.$key}`,
            imageID: 'Image001',
        })

        const defaultImage = parking.defaultImage || ''

        return new OmnisendCreateProductRequest({
            id: parking.$key,
            title: parking.title,
            status: 'inStock',
            description: parking.description?.substring(0, 300),
            currency: 'ZAR',
            url: `${environment.host}/parking/${parking.$key}`,
            type: parking.niceType,
            createdAt: moment().toISOString(),
            images: [defaultImage],
            categoryIDs: this.parkingCategoryIds(parking),
            variants: [omnisendVariant],
        })
    }

    /**
     * Map parking.niceType to the Omnisend product category IDs seeded by
     * seedOmnisendProductCategories. Keep in sync with PARKING_PRODUCT_CATEGORIES
     * in parkupp-functions/functions/src/omnisend/omnisend.functions.ts.
     */
    private parkingCategoryIds(parking: Parking): string[] {
        const map: Record<string, string> = {
            Monthly: 'monthly',
            Collector: 'collector',
            Events: 'events',
            Flex: 'flex',
        }
        const id = map[parking.niceType]
        return id ? [id] : []
    }

    /**
     * Sync the Omnisend browser cookie from the client's omnisendContactKey.
     * Contact creation is now handled server-side via the clientsAdmin Firestore trigger.
     */
    updateUserCookie(client: Client) {
        if (!client) return

        const existingContactId = this.globalService.getCookie(this.COOKIE_NAME)

        if (existingContactId && client.omnisendContactKey === existingContactId) return

        if (client.omnisendContactKey) {
            this.globalService.setCookie({
                name: this.COOKIE_NAME,
                value: client.omnisendContactKey,
                expireDays: 30,
            })
        }
    }

    /**
     * Get or set cart in Omnisend
     * Note: This is fire-and-forget - errors are logged but never thrown
     * to ensure tracking failures never block user flows
     */
    async getSetCart(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number; orderTotal: number }) {
        if (!this.hasEventContact(req.activeClient)) {
            console.warn('[Omnisend] getSetCart skipped - no email or phone on activeClient')
            return
        }
        try {
            if (req.activeClient.omnisendCartKey) {
                try {
                    const deleteCart = httpsCallable(this.functions, 'omnisend-deleteCart')
                    await deleteCart({ cartId: req.activeClient.omnisendCartKey, email: req.activeClient.$email })
                } catch (error) {
                    console.warn('[Omnisend] Cart deletion failed (non-blocking):', error)
                }
            }
            await this.createCart(req)
        } catch (error) {
            // Log but never throw - tracking should never block user flows
            console.warn('[Omnisend] getSetCart failed (non-blocking):', error)
        }
    }

    private hasEventContact(client: Client | undefined | null): boolean {
        if (!client) return false
        return Boolean(client.$email || client.$phoneNumber)
    }

    /**
     * Create cart in Omnisend
     * Note: This is fire-and-forget - errors are logged but never thrown
     * to ensure tracking failures never block user flows
     */
    async createCart(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number; orderTotal: number }) {
        if (!this.hasEventContact(req.activeClient)) {
            console.warn('[Omnisend] createCart skipped - no email or phone on activeClient')
            return null
        }
        const data = new OmnisendCreateCartRequest({
            productID: req.parking.$key,
            variantID: req.parking.$key,
            title: req.parking.title,
            description: req.parking.description?.substring(0, 300),
            productUrl: `${environment.host}/parking/${req.parking.$key}`,
            cartID: req.subscriptionKey,
            email: req.activeClient.$email,
            phone: req.activeClient.$phoneNumber,
            contactID: req.activeClient.omnisendContactKey,
            cartRecoveryUrl: `${environment.host}/cart/${req.subscriptionKey}`,
            subscriptionKey: req.subscriptionKey,
            quantity: req.quantity,
            priceInCents: req.parking.defaultPriceInCents / 100,
            imageUrl: req.parking.defaultImage,
        })

        try {
            const createCart = httpsCallable(this.functions, 'omnisend-createCart')
            const result = await createCart(data)
            await this.clientService.updateClient({ omnisendCartKey: req.subscriptionKey }, req.activeClient.$key!)
            await this.addToCart(req)
            return result
        } catch (error) {
            // Log but never throw - tracking should never block user flows
            console.warn('[Omnisend] Cart creation failed (non-blocking):', error)
            return null
        }
    }

    /**
     * Add to cart in Omnisend
     * Note: This is fire-and-forget - errors are logged but never thrown
     * to ensure tracking failures never block user flows
     */
    async addToCart(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number; orderTotal: number }) {
        if (!this.hasEventContact(req.activeClient)) {
            console.warn('[Omnisend] addToCart skipped - no email or phone on activeClient')
            return null
        }
        const omnisendAddToCartRequest = new OmnisendAddToCartRequest({
            currency: 'ZAR',
            cartProductID: req.parking.$key,
            productID: req.parking.$key,
            variantID: req.parking.$key,
            title: req.parking.title,
            description: req.parking.description?.substring(0, 300),
            quantity: req.quantity,
            price: req.priceInCents / 100,
            productUrl: `${environment.host}/parking/${req.parking.$key}`,
            imageUrl: req.parking.defaultImage,
        })

        try {
            const addToCart = httpsCallable(this.functions, 'omnisend-addToCart')
            const result = await addToCart({ cartId: req.subscriptionKey, omnisendAddToCartRequest })
            return result
        } catch (error) {
            // Log but never throw - tracking should never block user flows
            console.warn('[Omnisend] Add to cart failed (non-blocking):', error)
            return null
        }
    }

    /**
     * Create product in Omnisend
     * Note: This is fire-and-forget - errors are logged but never thrown
     * to ensure tracking failures never block user flows
     */
    async createProduct(parking: Parking): Promise<{ data: { data: { id: string } } } | null> {
        if (!parking.defaultImage) {
            console.warn('[Omnisend] Cannot create product - parking has no default image (non-blocking)')
            return null
        }

        try {
            const createProduct = httpsCallable<OmnisendCreateProductRequest, { data: { id: string } }>(this.functions, 'omnisend-createProduct')
            const result = await createProduct(this.buildProductRequest(parking))
            return result
        } catch (error) {
            // Log but never throw - tracking should never block user flows
            console.warn('[Omnisend] Product creation failed (non-blocking):', error)
            return null
        }
    }

    /**
     * Update an existing Omnisend product (PUT).
     * Used by upsertProduct when parking.omnisendProductId is already set.
     */
    async updateProduct(parking: Parking): Promise<{ data: { data: { id: string } } } | null> {
        if (!parking.defaultImage) {
            console.warn('[Omnisend] Cannot update product - parking has no default image (non-blocking)')
            return null
        }

        try {
            const updateProduct = httpsCallable<OmnisendCreateProductRequest, { data: { id: string } }>(this.functions, 'omnisend-updateProduct')
            const result = await updateProduct(this.buildProductRequest(parking))
            return result
        } catch (error) {
            console.warn('[Omnisend] Product update failed (non-blocking):', error)
            return null
        }
    }

    /**
     * Track that the user started checkout (sits between addToCart and createOrder
     * in Omnisend's abandoned-cart funnel). Without this event, abandoned-cart
     * automations targeting the started-checkout stage never fire.
     *
     * Fire-and-forget - errors are logged but never thrown.
     */
    async startCheckout(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number }) {
        if (!this.hasEventContact(req.activeClient)) {
            console.warn('[Omnisend] startCheckout skipped - no email or phone on activeClient')
            return null
        }

        const lineItem = {
            productID: req.parking.$key!,
            productVariantID: req.parking.$key,
            productTitle: req.parking.title,
            productDescription: req.parking.description?.substring(0, 300),
            productPrice: req.priceInCents / 100,
            productQuantity: req.quantity,
            productURL: `${environment.host}/parking/${req.parking.$key}`,
            productImageURL: req.parking.defaultImage,
        }

        try {
            const startCheckout = httpsCallable(this.functions, 'omnisend-startCheckout')
            return await startCheckout({
                cartId: req.subscriptionKey,
                email: req.activeClient.$email,
                phone: req.activeClient.$phoneNumber,
                lineItems: [lineItem],
            })
        } catch (error) {
            console.warn('[Omnisend] startCheckout failed (non-blocking):', error)
            return null
        }
    }

    /**
     * Create order in Omnisend (called after successful payment)
     * CRITICAL: This is fire-and-forget - errors are logged but NEVER thrown
     * to ensure tracking failures NEVER block successful subscription processing
     */
    async createOrder(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number; orderTotal: number }) {
        if (!this.hasEventContact(req.activeClient)) {
            console.warn('[Omnisend] createOrder skipped - no email or phone on activeClient')
            return null
        }
        const product = new OmnisendProduct({
            cartProductID: req.parking.$key,
            productID: req.parking.$key,
            variantID: req.parking.$key,
            title: req.parking.title,
            description: req.parking.description?.substring(0, 300),
            quantity: req.quantity,
            price: req.priceInCents / 100,
            productUrl: `${environment.host}/parking/${req.parking.$key}`,
            imageUrl: req.parking.defaultImage,
        })

        const requestData = new OmnisendCreateOrderRequest({
            orderID: req.subscriptionKey,
            email: req.activeClient.$email,
            phone: req.activeClient.$phoneNumber,
            firstName: req.activeClient.firstName,
            lastName: req.activeClient.lastName,
            cartID: req.subscriptionKey,
            orderUrl: `${environment.host}/client/${req.activeClient.$key}/subscription/${req.subscriptionKey}`,
            currency: 'ZAR',
            orderSum: req.orderTotal / 100,
            createdAt: moment().toISOString(),
            updatedAt: moment().toISOString(),
            paymentMethod: 'Credit card',
            paymentStatus: 'paid',
            fulfillmentStatus: 'fulfilled',
            products: [product],
        })

        try {
            const createOrder = httpsCallable(this.functions, 'omnisend-createOrder')
            const result = await createOrder(requestData)
            return result
        } catch (error) {
            // CRITICAL: Log but NEVER throw - tracking should NEVER block subscription completion
            console.warn('[Omnisend] Order creation failed (non-blocking) - subscription still succeeded:', error)
            return null
        }
    }
}
